import { useEffect, useRef } from 'react';

interface MetaTagsProps {
  title: string;
  description: string;
  canonicalUrl?: string;
  imageUrl?: string;
  // Add aliases for backward compatibility
  url?: string;  // Alias for canonicalUrl
  image?: string; // Alias for imageUrl
}

const MetaTags: React.FC<MetaTagsProps> = ({ 
  title, 
  description, 
  canonicalUrl, 
  imageUrl,
  url, // Alias for canonicalUrl 
  image // Alias for imageUrl
}) => {
  // Use a ref to track if meta tags have been set
  const metaTagsSetRef = useRef(false);
  
  useEffect(() => {
    // Mark that we're setting meta tags
    window.document.documentElement.setAttribute('data-setting-meta-tags', 'true');
    
    // Signal to GTM not to modify meta tags
    if (window.dataLayer) {
      window.dataLayer.push({
        'metaTagsBeingSet': true,
        'preserveMetaTags': true
      });
    }
    
    // Update title tag
    document.title = title;
    
    // Update meta tags
    updateMetaTag('description', description);
    
    // Update Open Graph meta tags for social sharing
    updateMetaTag('og:title', title);
    updateMetaTag('og:description', description);
    updateMetaTag('og:type', 'website');
    
    // Use canonicalUrl or its alias url, defaulting to current URL if none provided
    const finalCanonicalUrl = canonicalUrl || url || window.location.href;
    
    // Set og:url property - most important tag for sharing
    updateMetaTag('og:url', finalCanonicalUrl, true);
    
    // Set og:site_name separately from title (this is the site name that appears below the title)
    updateMetaTag('og:site_name', 'FitnessAppSearch');
    
    // Use imageUrl or its alias image
    const finalImageUrl = imageUrl || image;
    if (finalImageUrl) {
      updateMetaTag('og:image', finalImageUrl);
      updateMetaTag('og:image:width', '1200');
      updateMetaTag('og:image:height', '630');
    }
    
    // Update Twitter Card meta tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    if (finalImageUrl) {
      updateMetaTag('twitter:image', finalImageUrl);
    }
    
    // Update canonical URL link element
    let canonicalElement = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonicalElement) {
      canonicalElement = document.createElement('link');
      canonicalElement.rel = 'canonical';
      document.head.appendChild(canonicalElement);
    }
    canonicalElement.href = finalCanonicalUrl;
    
    // Mark meta tags as set
    metaTagsSetRef.current = true;
    
    // Dispatch an event that meta tags have been set
    const event = new CustomEvent('meta_tags_set', { 
      detail: { 
        title, 
        description,
        url: finalCanonicalUrl,
        image: finalImageUrl
      } 
    });
    document.dispatchEvent(event);
    
    // Remove the setting attribute after a short delay
    setTimeout(() => {
      window.document.documentElement.removeAttribute('data-setting-meta-tags');
      
      // Signal to GTM that we're done setting meta tags but still want to preserve them
      if (window.dataLayer) {
        window.dataLayer.push({
          'metaTagsBeingSet': false,
          'preserveMetaTags': true
        });
      }
    }, 100);
    
    // Clean up function for component unmount
    return () => {
      // We don't need to reset meta tags here as they'll be updated by the next page
      // Resetting them could cause flickering during page transitions
    };
  }, [title, description, canonicalUrl, imageUrl, url, image]);

  const updateMetaTag = (name: string, content: string, isImportant = false) => {
    // For OpenGraph and Twitter tags, we use property attribute
    const isOgOrTwitter = name.startsWith('og:') || name.startsWith('twitter:');
    const selector = isOgOrTwitter 
      ? `meta[property="${name}"]` 
      : `meta[name="${name}"]`;
    
    let metaTag = document.querySelector(selector);
    
    if (!metaTag) {
      metaTag = document.createElement('meta');
      if (isOgOrTwitter) {
        metaTag.setAttribute('property', name);
      } else {
        metaTag.setAttribute('name', name);
      }
      
      // Add data attributes to protect important meta tags
      if (isImportant || isOgOrTwitter) {
        metaTag.setAttribute('data-protected', 'true');
      }
      
      document.head.appendChild(metaTag);
    } else {
      // Add protection attribute to existing tags
      if (isImportant || isOgOrTwitter) {
        metaTag.setAttribute('data-protected', 'true');
      }
    }
    
    // Set the content attribute regardless of whether it's property or name
    metaTag.setAttribute('content', content);
  };

  return null;
};

export default MetaTags;
export type { MetaTagsProps }; 
